import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { getSlaPolicies, saveSlaPolicies, getTicketProjects, errorMessage } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { PageHeading } from '../components/NotionUI';
import Select from '../components/Select';
import { PRIORITY_COLORS } from '../components/NotionUI';

const PRIORITIES = ['urgent', 'high', 'medium', 'low'];
const key = (projectId, priority) => `${projectId}:${priority}`;

// I tempi si scrivono in ore (anche decimali: 0,5 = mezz'ora) e si salvano in minuti.
const toHours = (minutes) => (minutes === null || minutes === undefined ? '' : String(Math.round((minutes / 60) * 100) / 100));
const toMinutes = (hours) => {
  const n = Number(String(hours).replace(',', '.'));
  return hours === '' || Number.isNaN(n) || n <= 0 ? null : Math.round(n * 60);
};

function SlaSettings({ user, onLogout }) {
  const { t } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [cells, setCells] = useState({}); // "progetto:priorità" -> { first, resolution } in ore (testo)
  const [scope, setScope] = useState('0');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sla, proj] = await Promise.all([getSlaPolicies(), getTicketProjects()]);
      setProjects(proj.projects || []);
      const next = {};
      (sla.policies || []).forEach((p) => {
        next[key(p.project_id, p.priority)] = { first: toHours(p.first_response_minutes), resolution: toHours(p.resolution_minutes) };
      });
      setCells(next);
    } catch (err) {
      setError(errorMessage(err, t('slaSettings.loadError')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const cell = (projectId, priority) => cells[key(projectId, priority)] || { first: '', resolution: '' };
  const setCell = (priority, field, value) => {
    setSuccess('');
    setCells({ ...cells, [key(scope, priority)]: { ...cell(scope, priority), [field]: value } });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const policies = Object.entries(cells).map(([k, v]) => {
        const [projectId, priority] = k.split(':');
        return { project_id: Number(projectId), priority, first_response_minutes: toMinutes(v.first), resolution_minutes: toMinutes(v.resolution) };
      });
      await saveSlaPolicies(policies);
      setSuccess(t('slaSettings.saved'));
      await load();
    } catch (err) {
      setError(errorMessage(err, t('slaSettings.saveError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="nt-page">
        <PageHeading icon="clock" title={t('slaSettings.title')} subtitle={t('slaSettings.subtitle')} />
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {loading ? (
          <div className="loading">{t('common.loading')}</div>
        ) : (
          <>
            <div className="form-group" style={{ maxWidth: 360 }}>
              <label>{t('slaSettings.scope')}</label>
              <Select value={scope} onChange={(e) => setScope(e.target.value)}>
                <option value="0">{t('slaSettings.scopeCompany')}</option>
                {projects.map((p) => <option key={p.id} value={String(p.id)}>{t('slaSettings.scopeQueue', { name: p.name })}</option>)}
              </Select>
            </div>
            <p className="tp-hint">{scope === '0' ? t('slaSettings.hintCompany') : t('slaSettings.hintQueue')}</p>

            <div className="nt-table-wrap">
              <table className="nt-table cu-table sla-edit">
                <thead>
                  <tr>
                    <th>{t('slaSettings.priority')}</th>
                    <th>{t('slaSettings.firstResponse')}</th>
                    <th>{t('slaSettings.resolution')}</th>
                  </tr>
                </thead>
                <tbody>
                  {PRIORITIES.map((p) => {
                    const own = cell(scope, p);
                    const inherited = scope !== '0' ? cell(0, p) : null;
                    return (
                      <tr key={p}>
                        <td><span className="tk-prio" style={{ '--c': PRIORITY_COLORS[p] }}><i aria-hidden="true" /> {t(`tickets.priority_${p}`)}</span></td>
                        <td>
                          <input type="text" inputMode="decimal" className="sla-input" value={own.first} placeholder={inherited && inherited.first ? t('slaSettings.inherits', { hours: inherited.first }) : t('slaSettings.none')} onChange={(e) => setCell(p, 'first', e.target.value)} />
                          <span className="nt-muted"> {t('slaSettings.hours')}</span>
                        </td>
                        <td>
                          <input type="text" inputMode="decimal" className="sla-input" value={own.resolution} placeholder={inherited && inherited.resolution ? t('slaSettings.inherits', { hours: inherited.resolution }) : t('slaSettings.none')} onChange={(e) => setCell(p, 'resolution', e.target.value)} />
                          <span className="nt-muted"> {t('slaSettings.hours')}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="tp-hint">{t('slaSettings.note')}</p>
            <div className="tp-savebar">
              <button type="button" className="btn" disabled={saving} onClick={handleSave}>{saving ? t('common.saving') : t('common.save')}</button>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

export default SlaSettings;
